import type { RouteHandle } from '@app/routes/handle';
import { env } from '@config/env';
import { tDynamic } from '@i18n/helpers';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatches } from 'react-router-dom';

import { useTitleBadgeValue } from './titleBadge';

/**
 * Mirrors the deepest route's `handle.titleKey` (via i18next) to
 * `document.title`, with the app name appended; a view's title badge
 * («(3) Zu prüfen», see titleBadge.ts) takes the title's place while set.
 * Mount once inside the router — e.g. in `<AppShell>`.
 */
export function DocumentTitle() {
  const matches = useMatches();
  const { t, i18n } = useTranslation();
  const badge = useTitleBadgeValue();

  useEffect(() => {
    const titleKey = [...matches]
      .reverse()
      .map((m) => (m.handle as RouteHandle | undefined)?.titleKey)
      .find((k): k is string => typeof k === 'string' && k.length > 0);

    const translated = badge ?? (titleKey ? tDynamic(t, titleKey) : null);
    document.title = translated ? `${translated} · ${env.VITE_APP_NAME}` : env.VITE_APP_NAME;
    // `i18n.language` participates so the title is re-evaluated on lang change.
  }, [badge, matches, t, i18n.language]);

  return null;
}
